/**
 * get system theme
 * @returns {'light'|'dark'|undefined}
 */
export const getTheme = (): 'light' | 'dark' | undefined => {
  if (typeof window === 'undefined') return undefined;
  const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isLight = window.matchMedia('(prefers-color-scheme: light)').matches;
  return isDark ? 'dark' : isLight ? 'light' : undefined;
};

export const setTheme = (theme: 'light' | 'dark' | string) => {
  if (typeof window === 'undefined') return;
  window.document.documentElement.classList.remove('light', 'dark');
  window.document.documentElement.classList.add(theme);
};

/**
 * scroll to id
 * @param id
 */
export const scrollToAnchor = (id: string) => {
  if (typeof window === 'undefined') return;
  if (id) document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
};

/**
 * ScrollBehavior that respects the user's prefers-reduced-motion setting
 * (dpin-redesign-design.md §8: ticker/carousel must degrade under reduced motion).
 * @returns {ScrollBehavior} 'auto' when reduced motion is requested, else 'smooth'
 */
export const getScrollBehavior = (): ScrollBehavior => {
  if (typeof window === 'undefined') return 'smooth';
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
};

/**
 * get search params
 * @param {sting} name
 * @returns
 */
export const getSearchParam = (name: string) => {
  return typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get(name) : undefined;
};

/**
 * set search patam
 * @param name
 * @param value
 */
export const setSearchParam = (name: string, value: string) => {
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    url.searchParams.set(name, value);
    history.pushState({}, '', url);
  }
};

/**
 * @returns  {isMobile,isPC,isAndroid,isIOS}
 */
export const getOS = () => {
  const ua = navigator.userAgent;
  const isIOS = /iphone|ipad|ipod|ios/i.test(ua);
  const isAndroid = /android|XiaoMi|MiuiBrowser/i.test(ua);
  const isWindows = /windows|win32/i.test(ua);
  const isMac = /macintosh|macintel/i.test(ua);
  const isLinux = /linux/i.test(ua);
  const isMobile = isIOS || isAndroid;
  const isPC = !isMobile;
  return {
    isMobile,
    isPC,
    isWindows,
    isMac,
    isLinux,
    isAndroid,
    isIOS,
  };
};

/**
 * get system language
 * @returns {string|undefined}
 */
export const getLanguage = (): string | undefined => {
  return typeof window !== 'undefined' ? window.navigator.language : undefined;
};

/**
 *
 * @param lang
 * @param direction
 */
export const setLocale = (lang: string, direction = 'ltr') => {
  document.documentElement.setAttribute('lang', lang);
  window.document.documentElement.setAttribute('dir', direction);
};

/**
 *
 * @returns { lang?:string,dir?:string }
 */
export const getLocale = () => {
  return {
    lang: document.documentElement.getAttribute('lang'),
    dir: window.document.documentElement.getAttribute('dir'),
  };
};
